import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiCreditCard, FiPackage } from "react-icons/fi";

import SectionHeader from "../components/ui/SectionHeader";
import StatusBadge from "../components/ui/StatusBadge";
import {
  useAllocateStockForOrderMutation,
  useCreatePaymentMutation,
  useDeletePaymentMutation,
  useDeleteStockMovementMutation,
  useGetOrderAllocationsQuery,
  useGetOrdersQuery,
  useGetPaymentsQuery,
  useGetStockItemsQuery,
  useUpdateOrderMutation,
} from "../services/api";
import { getApiErrorMessage } from "../utils/apiError";
import { money, shortDate } from "../utils/formatters";

const ORDER_STATUSES = [
  "pending","design_review","approved",
  "in_production","quality_check","ready","delivered","cancelled",
];

const PAYMENT_METHODS = [
  { value: "cash",          label: "Cash" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "card",          label: "Card" },
  { value: "mobile_money",  label: "Mobile Money" },
];

const EMPTY_PAYMENT = { amount: "", payment_method: "cash", reference_number: "", notes: "" };
const EMPTY_ALLOC   = { stock_item_id: "", quantity: "", reason: "", note: "" };

// ── Small info tile ──────────────────────────────────────────
const Tile = ({ label, value, tone }) => (
  <div
    style={{
      background: "var(--surface-alt,#f3f4f6)",
      borderRadius: 10,
      padding: "0.55rem 0.85rem",
      color: tone === "danger" ? "var(--danger)" : tone === "success" ? "var(--success)" : "inherit",
    }}
  >
    <p style={{ margin: 0, fontSize: "0.72rem", color: "var(--muted,#888)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</p>
    <strong style={{ fontSize: "0.95rem" }}>{value}</strong>
  </div>
);

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const navigate    = useNavigate();

  const { data: ordersData }                          = useGetOrdersQuery();
  const { data: stockData }                           = useGetStockItemsQuery();
  const { data: paymentsData }                        = useGetPaymentsQuery();
  const { data: allocData, isLoading: allocLoading }  = useGetOrderAllocationsQuery(orderId);

  const [allocateStock]  = useAllocateStockForOrderMutation();
  const [deleteMovement] = useDeleteStockMovementMutation();
  const [createPayment]  = useCreatePaymentMutation();
  const [deletePayment]  = useDeletePaymentMutation();
  const [updateOrder]    = useUpdateOrderMutation();

  const [tab, setTab]               = useState("payments"); // payments | stock
  const [payForm, setPayForm]       = useState(EMPTY_PAYMENT);
  const [allocForm, setAllocForm]   = useState(EMPTY_ALLOC);
  const [statusValue, setStatusValue] = useState("");
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState("");

  const order       = ordersData?.data?.find((o) => Number(o.id) === Number(orderId));
  const stockItems  = stockData?.data  || [];
  const allocations = allocData?.data  || [];
  const allPayments = paymentsData?.data || [];
  const payments    = allPayments.filter((p) => Number(p.order_id) === Number(orderId));

  const selectedItem = stockItems.find((i) => Number(i.id) === Number(allocForm.stock_item_id));
  const totalAllocCost = allocations.reduce((s, a) => s + Number(a.quantity) * Number(a.unit_cost || 0), 0);

  const flash = (msg, isError = false) => {
    if (isError) setError(msg); else setSuccess(msg);
    setTimeout(() => { setError(""); setSuccess(""); }, 3500);
  };

  // ── Handlers ────────────────────────────────────────────────
  const handlePayment = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await createPayment({ order_id: orderId, ...payForm }).unwrap();
      setPayForm(EMPTY_PAYMENT);
      flash("Payment recorded.");
    } catch (err) {
      flash(getApiErrorMessage(err, "Unable to record payment."), true);
    }
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm("Delete this payment? The order balance will be recalculated.")) return;
    try {
      await deletePayment(id).unwrap();
      flash("Payment deleted.");
    } catch (err) {
      flash(getApiErrorMessage(err, "Unable to delete payment."), true);
    }
  };

  const handleAllocate = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await allocateStock({ orderId, ...allocForm }).unwrap();
      setAllocForm(EMPTY_ALLOC);
      flash("Material allocated.");
    } catch (err) {
      flash(getApiErrorMessage(err, "Unable to allocate stock."), true);
    }
  };

  const handleRemoveAlloc = async (movementId) => {
    if (!window.confirm("Remove this allocation? Quantity returns to stock.")) return;
    try {
      await deleteMovement(movementId).unwrap();
      flash("Allocation removed.");
    } catch (err) {
      flash(getApiErrorMessage(err, "Unable to remove allocation."), true);
    }
  };

  const handleStatusChange = async () => {
    if (!statusValue) return;
    try {
      await updateOrder({ id: orderId, order_status: statusValue }).unwrap();
      setStatusValue("");
      flash("Status updated.");
    } catch (err) {
      flash(getApiErrorMessage(err, "Unable to update status."), true);
    }
  };

  if (!order) {
    return (
      <div className="dashboard-page">
        <p style={{ color: "var(--muted,#888)" }}>Order not found or still loading…</p>
        <button className="secondary-button" type="button" onClick={() => navigate("/orders")}>
          ← Back to orders
        </button>
      </div>
    );
  }

  const balance = Number(order.remaining_balance);

  return (
    <div className="dashboard-page">
      <SectionHeader
        eyebrow={`Order #${order.id}`}
        title={order.title}
        action={
          <button className="secondary-button" type="button" onClick={() => navigate("/orders")}>
            <FiArrowLeft style={{ marginRight: 6, verticalAlign: "middle" }} />
            Back
          </button>
        }
      />

      {/* ── Flash messages ── */}
      {error   && <div className="flash flash--error">{error}</div>}
      {success && <div className="flash flash--success">{success}</div>}

      {/* ── Order header card ── */}
      <section className="panel order-detail-header">
        <div className="order-detail-header__top">
          <div>
            <p style={{ margin: "0 0 4px", color: "var(--muted,#888)", fontSize: "0.82rem" }}>
              {order.product_type} &bull; Customer: <strong>{order.customer_name || `#${order.customer_id}`}</strong>
            </p>
            {order.description && (
              <p style={{ margin: 0, color: "var(--muted,#888)", fontSize: "0.88rem" }}>{order.description}</p>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <StatusBadge value={order.order_status} />
            <StatusBadge value={order.payment_status} />
            {order.is_overdue && <StatusBadge value="overdue" />}
          </div>
        </div>

        {/* Tiles */}
        <div className="order-detail-tiles">
          <Tile label="Quantity"  value={Number(order.quantity).toLocaleString()} />
          <Tile label="Unit price" value={money(order.unit_price)} />
          <Tile label="Total"     value={money(order.total_price)} />
          <Tile label="Paid"      value={money(order.paid_amount)}      tone="success" />
          <Tile label="Balance"   value={money(balance)}                tone={balance > 0 ? "danger" : "success"} />
          <Tile label="Deadline"  value={shortDate(order.deadline)}     tone={order.is_overdue ? "danger" : ""} />
          <Tile label="Priority"  value={order.priority} />
          <Tile label="Assigned"  value={order.assigned_employee_name || "—"} />
        </div>

        {/* Quick status update */}
        <div className="order-status-update">
          <select
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value)}
          >
            <option value="">Change status…</option>
            {ORDER_STATUSES.map((s) => (
              <option key={s} value={s} disabled={s === order.order_status}>
                {s.replace(/_/g, " ")}
              </option>
            ))}
          </select>
          <button
            className="primary-button"
            type="button"
            onClick={handleStatusChange}
            disabled={!statusValue}
          >
            Update status
          </button>
        </div>
      </section>

      {/* ── Tab switcher ── */}
      <div className="order-tabs">
        <button
          type="button"
          className={`order-tab${tab === "payments" ? " order-tab--active" : ""}`}
          onClick={() => setTab("payments")}
        >
          <FiCreditCard style={{ marginRight: 6, verticalAlign: "middle" }} />
          Payments ({payments.length})
        </button>
        <button
          type="button"
          className={`order-tab${tab === "stock" ? " order-tab--active" : ""}`}
          onClick={() => setTab("stock")}
        >
          <FiPackage style={{ marginRight: 6, verticalAlign: "middle" }} />
          Materials used ({allocations.length})
        </button>
      </div>

      {/* ══ PAYMENTS TAB ══════════════════════════════════════ */}
      {tab === "payments" && (
        <div className="dashboard-grid dashboard-grid--wide">
          {/* List */}
          <section className="panel">
            <h3>Payment history</h3>

            {payments.length === 0 && (
              <p style={{ color: "var(--muted,#888)", fontSize: "0.9rem" }}>
                No payments recorded yet.
              </p>
            )}

            {payments.map((p) => (
              <div key={p.id} className="list-row">
                <div>
                  <strong>{money(p.amount)}</strong>
                  <p style={{ margin: "2px 0", fontSize: "0.82rem", color: "var(--muted,#888)" }}>
                    {p.payment_method?.replace("_", " ")} &bull; {shortDate(p.created_at)}
                  </p>
                  {p.reference_number && (
                    <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--muted,#888)" }}>
                      Ref: {p.reference_number}
                    </p>
                  )}
                  {p.notes && (
                    <p style={{ margin: 0, fontSize: "0.78rem", fontStyle: "italic" }}>{p.notes}</p>
                  )}
                </div>
                <button
                  className="table-action table-action--danger"
                  type="button"
                  onClick={() => handleDeletePayment(p.id)}
                >
                  Delete
                </button>
              </div>
            ))}

            {/* Totals */}
            {payments.length > 0 && (
              <div className="payment-totals">
                <div className="payment-totals__row">
                  <span>Order total</span>
                  <strong>{money(order.total_price)}</strong>
                </div>
                <div className="payment-totals__row">
                  <span>Total paid</span>
                  <strong style={{ color: "var(--success)" }}>{money(order.paid_amount)}</strong>
                </div>
                <div className="payment-totals__row payment-totals__row--balance">
                  <span>Remaining</span>
                  <strong style={{ color: balance > 0 ? "var(--danger)" : "var(--success)" }}>
                    {money(balance)}
                  </strong>
                </div>
              </div>
            )}
          </section>

          {/* Add payment form */}
          <form className="panel form-panel" onSubmit={handlePayment}>
            <h3>Record payment</h3>

            {balance <= 0 ? (
              <div className="flash flash--success" style={{ marginBottom: 0 }}>
                This order is fully paid. ✓
              </div>
            ) : (
              <p style={{ margin: "0 0 4px", fontSize: "0.85rem", color: "var(--muted,#888)" }}>
                Outstanding: <strong style={{ color: "var(--danger)" }}>{money(balance)}</strong>
              </p>
            )}

            <div className="form-field">
              <label>Amount</label>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={payForm.amount}
                onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                placeholder={`Max: ${money(balance)}`}
                required
              />
            </div>

            {/* Quick fill buttons */}
            {balance > 0 && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="table-action"
                  onClick={() => setPayForm({ ...payForm, amount: balance })}
                >
                  Full ({money(balance)})
                </button>
                <button
                  type="button"
                  className="table-action"
                  onClick={() => setPayForm({ ...payForm, amount: Math.round((balance / 2) * 100) / 100 })}
                >
                  Half ({money(balance / 2)})
                </button>
              </div>
            )}

            <div className="form-field">
              <label>Payment method</label>
              <select
                value={payForm.payment_method}
                onChange={(e) => setPayForm({ ...payForm, payment_method: e.target.value })}
              >
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            <input
              value={payForm.reference_number}
              onChange={(e) => setPayForm({ ...payForm, reference_number: e.target.value })}
              placeholder="Reference number (optional)"
            />
            <textarea
              value={payForm.notes}
              onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
              placeholder="Notes (optional)"
              rows="2"
            />

            <button className="primary-button" type="submit">
              Record payment
            </button>
          </form>
        </div>
      )}

      {/* ══ STOCK / MATERIALS TAB ═════════════════════════════ */}
      {tab === "stock" && (
        <div className="dashboard-grid dashboard-grid--wide">
          {/* Allocations list */}
          <section className="panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
              <h3 style={{ margin: 0 }}>Materials used</h3>
              <span style={{ fontSize: "0.82rem", color: "var(--muted,#888)" }}>
                Total cost: <strong>{money(totalAllocCost)}</strong>
              </span>
            </div>

            {allocLoading && <p>Loading…</p>}

            {!allocLoading && allocations.length === 0 && (
              <p style={{ color: "var(--muted,#888)", fontSize: "0.9rem" }}>
                No materials allocated yet.
              </p>
            )}

            {allocations.map((alloc) => (
              <div key={alloc.id} className="list-row">
                <div>
                  <strong>{alloc.item_name || `Item #${alloc.stock_item_id}`}</strong>
                  <p style={{ margin: "2px 0", fontSize: "0.82rem" }}>
                    Reason: <em>{alloc.reason}</em>
                  </p>
                  {alloc.note && (
                    <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--muted,#888)" }}>{alloc.note}</p>
                  )}
                </div>
                <div className="mini-metrics">
                  <span>
                    {alloc.quantity} {alloc.pricing_method === "per_meter" ? "m" : "pc"}
                  </span>
                  <span style={{ fontSize: "0.82rem" }}>
                    {money(Number(alloc.quantity) * Number(alloc.unit_cost || 0))}
                  </span>
                  <button
                    className="table-action table-action--danger"
                    type="button"
                    onClick={() => handleRemoveAlloc(alloc.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </section>

          {/* Allocate form */}
          <form className="panel form-panel" onSubmit={handleAllocate}>
            <h3>Take material from stock</h3>
            <p style={{ margin: "0 0 8px", fontSize: "0.85rem", color: "var(--muted,#888)" }}>
              Quantity is deducted from stock immediately.
            </p>

            <div className="form-field">
              <label>Material</label>
              <select
                value={allocForm.stock_item_id}
                onChange={(e) => setAllocForm({ ...allocForm, stock_item_id: e.target.value })}
                required
              >
                <option value="">— Select material —</option>
                {stockItems.map((item) => (
                  <option key={item.id} value={item.id} disabled={Number(item.quantity) <= 0}>
                    {item.name} — {item.quantity} {item.unit} available
                    {Number(item.quantity) <= 0 ? " (out of stock)" : item.low_stock ? " ⚠ low" : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label>
                  Quantity
                  {selectedItem ? ` (${selectedItem.pricing_method === "per_meter" ? "m" : "pc"})` : ""}
                </label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={allocForm.quantity}
                  onChange={(e) => setAllocForm({ ...allocForm, quantity: e.target.value })}
                  placeholder={selectedItem ? `Max: ${selectedItem.quantity}` : "Qty"}
                  required
                />
              </div>
              {selectedItem && Number(allocForm.quantity) > 0 && (
                <div style={{ background: "var(--surface-alt,#f3f4f6)", borderRadius: 8, padding: "0.5rem 0.75rem", fontSize: "0.85rem", display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <span style={{ color: "var(--muted,#888)" }}>Est. cost</span>
                  <strong>{money(Number(allocForm.quantity) * Number(selectedItem.buy_price || selectedItem.unit_cost || 0))}</strong>
                </div>
              )}
            </div>

            <div className="form-field">
              <label>Reason <span style={{ color: "var(--danger)" }}>*</span></label>
              <input
                value={allocForm.reason}
                onChange={(e) => setAllocForm({ ...allocForm, reason: e.target.value })}
                placeholder="e.g. Printing 3000 flyers for this order"
                required
              />
            </div>

            <textarea
              value={allocForm.note}
              onChange={(e) => setAllocForm({ ...allocForm, note: e.target.value })}
              placeholder="Additional notes (optional)"
              rows="2"
            />

            <button className="primary-button" type="submit">
              Allocate to order
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default OrderDetailPage;
